import React, { useEffect, useRef, useState } from 'react';
import { Prompt } from 'react-router-dom';
import cogoToast from 'cogo-toast';
import Select from 'react-select';
import { getCirclePlacement, publishCirclePlacement, getCircleCategories, saveCircle } from '../../api/circles';
import OrderedRows from './OrderedRows';
import FeaturedBrands from './FeaturedBrands';
import CircleEditor from './CircleEditor';
import CircleInfo from './CircleInfo';
import CircleCategory, { CATEGORY_HINT } from './CircleCategory';
import CircleResult from './CircleResult';
import SearchResults from './SearchResults';
import useSearch from './useSearch';
import { message, confirmDiscard, CIRCLE_PLACEMENT_KEYS, withCircleAssignment } from './circle_helpers';
import './Circles.scss';

const featureValues = rows =>
  rows.map(row => ({
    CuratorGroup_id: row.circle.id,
    imageOverride: row.imageOverride || null,
    imageOverrideNonText: row.imageOverrideNonText || null,
    descriptionOverride: row.descriptionOverride || null
  }));

export default function Circles() {
  const [view, setView] = useState('placements');
  const [placement, setPlacement] = useState('onboarding');
  const [onboardingEntity, setOnboardingEntity] = useState('circles');
  const showBrands = placement === 'onboarding' && onboardingEntity === 'brands';
  const [placements, setPlacements] = useState({});
  const [saved, setSaved] = useState({});
  const [drafts, setDrafts] = useState({});
  const [undo, setUndo] = useState({});
  const [editor, setEditor] = useState(null);
  const [query, setQuery] = useState('');
  const [libraryPage, setLibraryPage] = useState(0);
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [placementFilter, setPlacementFilter] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);
  const [brandsDirty, setBrandsDirty] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [industries, setIndustries] = useState([]);
  const [categoriesReady, setCategoriesReady] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [categoryAttempt, setCategoryAttempt] = useState(0);
  const rows = drafts[placement] || [];
  const placementShortcuts = ['onboarding', 'web_home_circles', 'app_home_wheel'].filter(key => placements[key]);
  const dirty = key => saved[key] && JSON.stringify(featureValues(drafts[key] || [])) !== JSON.stringify(featureValues(saved[key].features));
  const anyDirty = Object.keys(saved).some(dirty);
  const addsPlacements = Object.keys(saved).some(key =>
    (drafts[key] || []).some(row => !saved[key].features.some(feature => feature.circle.id === row.circle.id))
  );
  const search = useSearch(query, view === 'library' ? libraryPage : 0, refresh, {
    visibility: view === 'library' ? visibilityFilter : 'all',
    placement: view === 'library' ? placementFilter : ''
  });
  const libraryEnd = useRef(null);
  const refreshSearch = () => {
    setLibraryPage(0);
    setRefresh(value => value + 1);
  };

  useEffect(() => {
    if (categoriesReady) return;
    let current = true;
    setCategoryError('');
    getCircleCategories()
      .then(data => {
        if (!current) return;
        setIndustries(data.industries);
        setCategoriesReady(true);
      })
      .catch(() => current && setCategoryError('Could not load categories.'));
    return () => {
      current = false;
    };
  }, [categoriesReady, categoryAttempt]);

  useEffect(() => {
    if (view !== 'library' || !libraryEnd.current || search.loading || search.error || !search.hasMore || busy || editor) return;
    let requested = false;
    const observer = new IntersectionObserver(
      entries => {
        if (!requested && entries.some(entry => entry.isIntersecting)) {
          requested = true;
          setLibraryPage(value => value + 1);
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(libraryEnd.current);
    return () => observer.disconnect();
  }, [view, libraryPage, search.loading, search.error, search.hasMore, busy, editor]);

  useEffect(() => {
    const missing = (editor || view === 'library' ? CIRCLE_PLACEMENT_KEYS : [placement]).filter(key => !saved[key]);
    if (!missing.length) return;
    let current = true;
    setLoadError('');
    Promise.all(missing.map(async key => [key, await getCirclePlacement(key)]))
      .then(entries => {
        if (!current) return;
        setPlacements(entries[0][1].placements);
        setSaved(old => ({ ...old, ...Object.fromEntries(entries) }));
        setDrafts(old => ({
          ...old,
          ...Object.fromEntries(entries.map(([key, data]) => [key, data.features.map(row => ({ ...row, id: row.circle.id }))]))
        }));
      })
      .catch(error => current && setLoadError(message(error)));
    return () => {
      current = false;
    };
  }, [placement, saved, refresh, editor, view]);

  useEffect(() => {
    const warn = event => {
      if (anyDirty || brandsDirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anyDirty, brandsDirty]);

  const changeRows = next => {
    setDrafts(old => ({ ...old, [placement]: next }));
    setUndo(old => ({ ...old, [placement]: null }));
  };
  const remove = ids => {
    setUndo(old => ({ ...old, [placement]: rows }));
    setDrafts(old => ({ ...old, [placement]: rows.filter(row => !ids.includes(row.id)) }));
  };
  const acceptPublication = (key, data, preserveDrafts = false) => {
    const fresh = new Map(data.features.map(row => [row.circle.id, row.circle]));
    const refreshCircles = list => list.map(row => (fresh.has(row.circle.id) ? { ...row, circle: fresh.get(row.circle.id) } : row));
    setSaved(old => ({
      ...Object.fromEntries(Object.entries(old).map(([name, value]) => [name, { ...value, features: refreshCircles(value.features) }])),
      [key]: data
    }));
    setDrafts(old => ({
      ...Object.fromEntries(Object.entries(old).map(([name, list]) => [name, refreshCircles(list)])),
      [key]: preserveDrafts ? refreshCircles(old[key]) : data.features.map(row => ({ ...row, id: row.circle.id }))
    }));
    setUndo(old => ({
      ...Object.fromEntries(Object.entries(old).map(([name, list]) => [name, list ? refreshCircles(list) : list])),
      ...(!preserveDrafts && { [key]: null })
    }));
    setEditor(old => (old && fresh.has(old.id) ? fresh.get(old.id) : old));
  };
  const publish = async () => {
    setBusy(true);
    try {
      const data = await publishCirclePlacement(placement, featureValues(rows), saved[placement].revision);
      acceptPublication(placement, data);
      refreshSearch();
      cogoToast.success('Changes published. This placement is live.');
    } catch (error) {
      cogoToast.error(message(error));
    } finally {
      setBusy(false);
    }
  };
  const stageCirclePlacements = (circle, selectedPlacements) => {
    const changed = CIRCLE_PLACEMENT_KEYS.filter(
      key => (drafts[key] || []).some(row => row.circle.id === circle.id) !== selectedPlacements.includes(key)
    );
    setDrafts(old =>
      Object.fromEntries(Object.entries(old).map(([key, list]) => [key, withCircleAssignment(list, circle, selectedPlacements.includes(key))]))
    );
    setUndo(old => ({ ...old, ...Object.fromEntries(changed.map(key => [key, null])) }));
    return changed;
  };
  const publishAll = async () => {
    setBusy(true);
    try {
      for (const key of CIRCLE_PLACEMENT_KEYS.filter(dirty)) {
        let data;
        try {
          data = await publishCirclePlacement(key, featureValues(drafts[key]), saved[key].revision);
        } catch (error) {
          throw new Error(`${placements[key]?.label} changes were not saved: ${message(error)}. Remaining changes are still pending.`);
        }
        acceptPublication(key, data);
      }
      cogoToast.success(addsPlacements ? 'Circle placement changes published.' : 'Circle placement changes saved.');
    } catch (error) {
      cogoToast.error(message(error));
    } finally {
      refreshSearch();
      setBusy(false);
    }
  };
  const onCircleSaved = async (circle, selectedPlacements, action) => {
    const keys = CIRCLE_PLACEMENT_KEYS;
    const changed = stageCirclePlacements(circle, selectedPlacements);
    const refreshCircle = list => list.map(row => (row.circle.id === circle.id ? { ...row, circle } : row));
    setSaved(old => Object.fromEntries(Object.entries(old).map(([key, data]) => [key, { ...data, features: refreshCircle(data.features) }])));
    setUndo(old => Object.fromEntries(Object.entries(old).map(([key, list]) => [key, list ? refreshCircle(list) : list])));
    const unpublished = keys.filter(key => saved[key].features.some(row => row.circle.id === circle.id) !== selectedPlacements.includes(key));
    setEditor(circle);
    setEditorDirty(false);
    refreshSearch();
    const affected = [...new Set([...changed, ...unpublished])];
    if (affected.length) {
      setPlacement(affected.find(key => selectedPlacements.includes(key)) || affected[0]);
      setView('placements');
      setOnboardingEntity('circles');
      setQuery('');
    }
    if (action !== 'publish') return;
    try {
      await publishCirclePlacements(circle, selectedPlacements);
    } catch (error) {
      throw new Error(`Circle saved. ${message(error)}`);
    }
  };
  const changeCategory = async (circle, category) => {
    setBusy(true);
    try {
      const { circle: updated } = await saveCircle({
        id: circle.id,
        updatedAt: circle.updatedAt,
        ...category
      });
      const refreshCircle = list => list.map(row => (row.circle.id === circle.id ? { ...row, circle: updated } : row));
      setSaved(old => Object.fromEntries(Object.entries(old).map(([key, data]) => [key, { ...data, features: refreshCircle(data.features) }])));
      setDrafts(old => Object.fromEntries(Object.entries(old).map(([key, list]) => [key, refreshCircle(list)])));
      setUndo(old => Object.fromEntries(Object.entries(old).map(([key, list]) => [key, list && refreshCircle(list)])));
      search.replaceCircle(updated);
      cogoToast.success('Primary category updated.');
    } catch (error) {
      cogoToast.error(message(error));
    } finally {
      setBusy(false);
    }
  };
  const publishCirclePlacements = async (circle, selectedPlacements) => {
    const unpublished = CIRCLE_PLACEMENT_KEYS.filter(
      key => saved[key].features.some(row => row.circle.id === circle.id) !== selectedPlacements.includes(key)
    );
    setBusy(true);
    try {
      for (const key of unpublished) {
        // Publish this Circle's assignment only; other local list edits remain drafts.
        const publishedRows = withCircleAssignment(saved[key].features, circle, selectedPlacements.includes(key));
        let data;
        try {
          data = await publishCirclePlacement(key, featureValues(publishedRows), saved[key].revision);
        } catch (error) {
          throw new Error(`${placements[key]?.label} was not published: ${message(error)}`);
        }
        acceptPublication(key, data, true);
      }
      refreshSearch();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className='circles-admin'>
      <Prompt when={anyDirty || editorDirty || brandsDirty} message='You have unpublished placement changes. Leave without publishing?' />
      <header className='circle-toolbar'>
        <div>
          <h1>Featured Content</h1>
          <p>Manage the Circles and brands shoppers discover.</p>
        </div>
        {(view !== 'placements' || !showBrands) && (
          <button
            className='primary'
            disabled={busy}
            onClick={() => setEditor({ title: '', description: '', coverImage: null, members: [], placements: [] })}
          >
            Create Circle
          </button>
        )}
      </header>
      <nav className='circle-toolbar' aria-label='Featured content views'>
        <button
          aria-pressed={view === 'library'}
          onClick={() => {
            setView('library');
            setLibraryPage(0);
            setQuery('');
          }}
        >
          Circle library
        </button>
        <button
          aria-pressed={view === 'placements'}
          onClick={() => {
            setView('placements');
            setQuery('');
          }}
        >
          Featured placements
        </button>
      </nav>
      {categoryError && !editor && (
        <p role='alert'>
          {categoryError} <button onClick={() => setCategoryAttempt(old => old + 1)}>Retry categories</button>
        </p>
      )}
      {!!placementShortcuts.length && (
        <nav className='circle-placement-shortcuts' aria-label='Placement shortcuts'>
          {placementShortcuts.map(key => (
            <button
              key={key}
              disabled={busy}
              aria-pressed={view === 'placements' && placement === key}
              onClick={() => {
                setView('placements');
                setPlacement(key);
                setOnboardingEntity('circles');
                setQuery('');
              }}
            >
              {placements[key].label}
            </button>
          ))}
        </nav>
      )}
      <section hidden={view !== 'placements'}>
        <div className='circle-controls circle-placement-filters'>
          <label>
            Placement
            <select
              value={placement}
              disabled={busy}
              onChange={event => {
                setPlacement(event.target.value);
                setQuery('');
              }}
            >
              {!Object.keys(placements).length && <option value={placement}>Loading placements…</option>}
              {CIRCLE_PLACEMENT_KEYS.filter(key => placements[key]).map(key => (
                <option key={key} value={key}>
                  {placements[key].label}
                  {dirty(key) || (key === 'onboarding' && brandsDirty) ? ' · unpublished changes' : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
        {placement === 'onboarding' && (
          <nav className='circle-toolbar' aria-label='Onboarding entity types'>
            <button aria-pressed={onboardingEntity === 'brands'} onClick={() => setOnboardingEntity('brands')}>
              Brands{brandsDirty ? ' · unpublished changes' : ''}
            </button>
            <button aria-pressed={onboardingEntity === 'circles'} onClick={() => setOnboardingEntity('circles')}>
              Circles{dirty('onboarding') ? ' · unpublished changes' : ''}
            </button>
          </nav>
        )}
        <FeaturedBrands visible={view === 'placements' && showBrands} onDirtyChange={setBrandsDirty} />
        {view === 'placements' && !showBrands && (
          <div className='circle-featured-placement'>
            <label>
              Add an existing Circle
              <input placeholder='Circle name or CuratorGroup ID' value={query} onChange={event => setQuery(event.target.value)} />
            </label>
            <p id={editor ? undefined : 'circle-category-hint'} className='circle-category-hint'>
              Primary category changes save automatically. {CATEGORY_HINT}
            </p>
            {!!query.trim() && (
              <SearchResults
                search={search}
                render={circle => (
                  <CircleResult key={circle.id} circle={circle}>
                    <button
                      disabled={busy || !saved[placement] || rows.some(row => row.id === circle.id) || rows.length >= placements[placement]?.limit}
                      onClick={() => changeRows([...rows, { id: circle.id, circle }])}
                    >
                      {rows.some(row => row.id === circle.id) ? 'Added' : 'Add Circle'}
                    </button>
                  </CircleResult>
                )}
              />
            )}
            {placements[placement] && (
              <div className='circle-placement-heading'>
                <div>
                  <h2>{placements[placement].label}</h2>
                  <p>{placements[placement].description}</p>
                </div>
                {saved[placement] && (
                  <span className='circle-placement-count'>
                    {rows.length} / {placements[placement].limit} Circles
                  </span>
                )}
              </div>
            )}
            {loadError ? (
              <p role='alert'>
                {loadError} <button onClick={() => refreshSearch()}>Retry</button>
              </p>
            ) : !saved[placement] ? (
              <p>Loading placement…</p>
            ) : (
              <>
                <OrderedRows
                  key={placement}
                  rows={rows}
                  onChange={changeRows}
                  onRemove={remove}
                  disabled={busy}
                  renderDragPreview={moving => (
                    <div className='circle-drag-preview'>
                      <CircleInfo circle={moving[0].circle} showVisibility={false} />
                      {moving.length > 1 && <span className='circle-drag-count'>+{moving.length - 1} more</span>}
                    </div>
                  )}
                  header={
                    <div className='circle-placement-columns'>
                      <span>Display Rank</span>
                      <div className='circle-placement-row-columns'>
                        <span>Circle</span>
                        <span>Primary category</span>
                      </div>
                    </div>
                  }
                  renderRow={row => (
                    <>
                      <CircleInfo circle={row.circle} description={row.descriptionOverride || row.circle.description} />
                      <div className='circle-category-control'>
                        <CircleCategory
                          circle={row.circle}
                          industries={industries}
                          disabled={busy || !categoriesReady}
                          label={`Primary category for ${row.circle.title}`}
                          onChange={category => changeCategory(row.circle, category)}
                        />
                      </div>
                      <div className='circle-actions'>
                        <button disabled={busy} onClick={() => setEditor(row.circle)}>
                          Edit Circle
                        </button>
                        <button disabled={busy} onClick={() => remove([row.id])}>
                          Remove
                        </button>
                      </div>
                    </>
                  )}
                />
                <footer className='circle-toolbar circle-placement-footer'>
                  <span className={`circle-placement-status ${dirty(placement) ? 'pending' : ''}`}>
                    {rows.length} / {placements[placement]?.limit} Circles · {dirty(placement) ? 'Unpublished changes' : 'Published'}
                  </span>
                  {undo[placement] && (
                    <button
                      disabled={busy}
                      onClick={() => {
                        setDrafts(old => ({ ...old, [placement]: undo[placement] }));
                        setUndo(old => ({ ...old, [placement]: null }));
                      }}
                    >
                      Undo removal
                    </button>
                  )}
                  <button
                    disabled={busy || !dirty(placement)}
                    onClick={() => {
                      changeRows(saved[placement].features.map(row => ({ ...row, id: row.circle.id })));
                      refreshSearch();
                    }}
                  >
                    Discard changes
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => {
                      const reload = () =>
                        setSaved(old => {
                          const next = { ...old };
                          delete next[placement];
                          return next;
                        });
                      if (dirty(placement)) confirmDiscard('Reload and discard unpublished changes for this placement?', reload);
                      else reload();
                    }}
                  >
                    Reload
                  </button>
                  <button disabled={busy || !dirty(placement)} className='primary' onClick={publish}>
                    {busy ? 'Publishing…' : 'Publish changes'}
                  </button>
                </footer>
              </>
            )}
          </div>
        )}
      </section>
      {view === 'library' && (
        <section>
          {brandsDirty && (
            <div className='circle-toolbar circle-library-actions' role='status'>
              <span>Onboarding brand changes remain unpublished. Publish or discard them in Brands.</span>
              <button
                onClick={() => {
                  setView('placements');
                  setPlacement('onboarding');
                  setOnboardingEntity('brands');
                  setQuery('');
                }}
              >
                Review brand changes
              </button>
            </div>
          )}
          {anyDirty && (
            <div className='circle-toolbar circle-library-actions'>
              <button
                disabled={busy}
                onClick={() => {
                  setDrafts(
                    Object.fromEntries(Object.entries(saved).map(([key, data]) => [key, data.features.map(row => ({ ...row, id: row.circle.id }))]))
                  );
                  setUndo({});
                }}
              >
                Clear Circle changes
              </button>
              <button className='primary' disabled={busy} onClick={publishAll}>
                {busy ? 'Saving changes…' : addsPlacements ? 'Publish all Circle changes' : 'Save all Circle changes'}
              </button>
            </div>
          )}
          <div className='circle-controls circle-library-filters'>
            <label>
              Search Circles
              <input
                placeholder='Circle name or CuratorGroup ID'
                value={query}
                onChange={event => {
                  setQuery(event.target.value);
                  setLibraryPage(0);
                }}
              />
            </label>
            <label>
              Visibility
              <select
                value={visibilityFilter}
                onChange={event => {
                  setVisibilityFilter(event.target.value);
                  setLibraryPage(0);
                }}
              >
                <option value='all'>All Circles</option>
                <option value='public'>Public only</option>
                <option value='hidden'>Hidden only</option>
              </select>
            </label>
            <label>
              Featured in
              <select
                value={placementFilter}
                onChange={event => {
                  setPlacementFilter(event.target.value);
                  setLibraryPage(0);
                }}
              >
                <option value=''>Any placement</option>
                {CIRCLE_PLACEMENT_KEYS.filter(key => placements[key]).map(key => (
                  <option key={key} value={key}>
                    {placements[key].label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {loadError && (
            <p role='alert'>
              {loadError} <button onClick={() => refreshSearch()}>Retry placements</button>
            </p>
          )}
          <p id={editor ? undefined : 'circle-category-hint'} className='circle-category-hint'>
            Primary category changes save automatically. {CATEGORY_HINT}
          </p>
          <SearchResults
            className='circle-library-results'
            header={
              <div className='circle-library-header'>
                <span>Circle</span>
                <span>Primary category</span>
                <span>Feature Placement</span>
              </div>
            }
            search={search}
            render={circle => {
              const keys = CIRCLE_PLACEMENT_KEYS;
              const ready = keys.every(key => saved[key]);
              const selected = keys.filter(key => (drafts[key] || []).some(row => row.circle.id === circle.id));
              const unpublished = ready && keys.some(key => selected.includes(key) !== saved[key].features.some(row => row.circle.id === circle.id));
              const addsPlacement = ready && selected.some(key => !saved[key].features.some(row => row.circle.id === circle.id));
              const publishFull = selected.some(
                key => !saved[key]?.features.some(row => row.circle.id === circle.id) && saved[key]?.features.length >= placements[key]?.limit
              );
              const options = keys
                .filter(key => placements[key])
                .map(key => ({
                  value: key,
                  label: placements[key]?.label,
                  isDisabled: !selected.includes(key) && (drafts[key] || []).length >= placements[key]?.limit
                }));
              return (
                <CircleResult
                  key={circle.id}
                  circle={circle}
                  categoryControl={
                    <CircleCategory
                      circle={circle}
                      industries={industries}
                      disabled={busy || !categoriesReady}
                      label={`Primary category for ${circle.title}`}
                      onChange={category => changeCategory(circle, category)}
                    />
                  }
                  placementControl={
                    <>
                      <Select
                        isMulti
                        isClearable
                        controlShouldRenderValue={false}
                        hideSelectedOptions={false}
                        closeMenuOnSelect={false}
                        blurInputOnSelect={false}
                        className='circle-placement-select'
                        classNamePrefix='circle-placement-select'
                        inputId={`circle-placement-${circle.id}`}
                        aria-label={`Feature placement for ${circle.title}`}
                        isDisabled={busy || !ready}
                        placeholder={ready ? (selected.length ? 'Edit placements' : 'Not featured') : 'Loading placements…'}
                        options={options}
                        value={options.filter(option => selected.includes(option.value))}
                        formatOptionLabel={(option, { context }) =>
                          context === 'menu' ? (
                            <>
                              <input
                                type='checkbox'
                                checked={selected.includes(option.value)}
                                disabled={option.isDisabled}
                                readOnly
                                tabIndex={-1}
                                aria-hidden='true'
                              />
                              {option.label}
                              {option.isDisabled ? ' (full)' : ''}
                            </>
                          ) : (
                            option.label
                          )
                        }
                        onChange={options =>
                          stageCirclePlacements(
                            circle,
                            (options || []).map(option => option.value)
                          )
                        }
                      />
                      {!!selected.length && (
                        <ul className='circle-placement-tags' aria-label={`Selected placements for ${circle.title}`}>
                          {selected.map(key => (
                            <li key={key}>
                              <span>{placements[key]?.label}</span>
                              <button
                                type='button'
                                aria-label={`Remove ${placements[key]?.label} from ${circle.title}`}
                                disabled={busy || !ready}
                                onClick={() =>
                                  stageCirclePlacements(
                                    circle,
                                    selected.filter(value => value !== key)
                                  )
                                }
                              >
                                <span aria-hidden='true'>×</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      {unpublished && publishFull && <small>Publish pending removals first.</small>}
                    </>
                  }
                >
                  <button disabled={busy} onClick={() => setEditor(circle)}>
                    Edit Circle
                  </button>
                  {unpublished && (
                    <button
                      className='primary'
                      disabled={busy || publishFull}
                      onClick={async () => {
                        try {
                          await publishCirclePlacements(circle, selected);
                          cogoToast.success(addsPlacement ? 'Circle placements published.' : 'Circle placements saved.');
                        } catch (error) {
                          cogoToast.error(message(error));
                        }
                      }}
                    >
                      {addsPlacement ? 'Publish' : 'Save'}
                    </button>
                  )}
                </CircleResult>
              );
            }}
          />
          <div ref={libraryEnd} className='circle-library-end' aria-hidden='true' />
          <div className='circle-toolbar'>
            {anyDirty && (
              <button
                onClick={() => {
                  setPlacement(CIRCLE_PLACEMENT_KEYS.find(dirty));
                  setOnboardingEntity('circles');
                  setView('placements');
                  setQuery('');
                }}
              >
                Review placement changes
              </button>
            )}
          </div>
        </section>
      )}
      {editor && (
        <CircleEditor
          circle={editor}
          industries={industries}
          categoriesReady={categoriesReady}
          categoryError={categoryError}
          onRetryCategories={() => setCategoryAttempt(old => old + 1)}
          placementOptions={CIRCLE_PLACEMENT_KEYS.filter(key => placements[key]).map(key => ({
            key,
            label: placements[key]?.label,
            selected: saved[key] ? (drafts[key] || []).some(row => row.circle.id === editor.id) : editor.placements?.includes(key),
            published: saved[key]?.features.some(row => row.circle.id === editor.id),
            full: (drafts[key] || []).length >= placements[key]?.limit,
            publishFull: saved[key]?.features.length >= placements[key]?.limit && !saved[key]?.features.some(row => row.circle.id === editor.id)
          }))}
          placementsReady={CIRCLE_PLACEMENT_KEYS.every(key => saved[key])}
          placementError={loadError}
          onRetryPlacements={() => refreshSearch()}
          onSaved={onCircleSaved}
          onDirtyChange={setEditorDirty}
          onClose={() => {
            setEditor(null);
            setEditorDirty(false);
          }}
        />
      )}
    </div>
  );
}
